/**
 * 기간 띠(설계 §4-1 개요) — 재원 · 휴원 · 반(반마다 한 줄) · 학교 · 부서를 달력 위 막대로, 오늘은 붉은 세로선.
 * 막대에는 글자를 넣지 않는다. 막대나 행 이름을 누르면 아래에서 전체 이름·날짜·일수를 확인한다.
 * 마우스오버의 title 도 유지한다. 위치 계산은 academyModel.buildLanes · lanePercent.
 *
 * 막대는 띠(relative)의 **직계 자식**이다 — 전역 preserve-3d 때문에 absolute 의 기준이 바로 위 부모가 된다(App.css).
 */
import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { lanePercent } from '../academyModel';
import { addDays } from '../periodDates';

const BAR_TONE = {
  cur: 'bg-primary text-white',
  past: 'bg-primary/30 text-primary-dark',
  pause: 'bg-yellow-300 text-yellow-900',
};
const barKey = (bar) => `${bar.start_on}-${bar.end_on}`;
const fullDate = (date) => date?.replace(/-/g, '.');

function PeriodLanes({ model }) {
  const { rangeStart, rangeEnd, lanes, ticks, today } = model;
  const axisRef = useRef(null);
  const [hiddenTicks, setHiddenTicks] = useState([]);
  const [selection, setSelection] = useState(null);
  const detailId = useId();
  const selectedLane = lanes.find((lane) => lane.key === selection?.lane);
  const selectedBars = selectedLane?.bars.filter((bar) => !selection.bar || barKey(bar) === selection.bar) || [];
  const select = (lane, bar = null) => setSelection((previous) => previous?.lane === lane && previous.bar === bar ? null : { lane, bar });
  useLayoutEffect(() => {
    const axis = axisRef.current;
    if (!axis) return undefined;
    const measure = () => {
      const bounds = axis.getBoundingClientRect();
      if (!bounds.width) return;
      let previousRight = bounds.left - 4;
      const hidden = [];
      [...axis.children].forEach((node, index) => {
        const rect = node.getBoundingClientRect();
        if (rect.left < previousRight + 4 || rect.right > bounds.right + 1) hidden.push(ticks[index].date);
        else previousRight = rect.right;
      });
      setHiddenTicks((prev) => (prev.length === hidden.length && prev.every((date, i) => date === hidden[i]) ? prev : hidden));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(axis);
    [...axis.children].forEach((node) => observer?.observe(node));
    return () => observer?.disconnect();
  }, [ticks]);
  const pct = (d) => lanePercent(d, rangeStart, rangeEnd);
  const todayPct = pct(addDays(today, 1));
  if (!lanes.some((lane) => lane.bars.length > 0)) {
    return <p className="py-2 text-xs text-gray-500">표시할 기간 기록이 없습니다.</p>;
  }
  return (
    <div className="space-y-1" role="group" aria-label="재원·휴원·반·학교·부서 기간">
      {lanes.map((lane) => (
        <div key={lane.key} className="flex items-center gap-2">
          <button type="button" disabled={!lane.bars.length} title={`${lane.label} 기간 보기`}
            aria-label={`${lane.label} 기간 보기`} aria-expanded={selectedLane?.key === lane.key && selectedBars.length > 0}
            aria-controls={detailId} onClick={() => select(lane.key)}
            className="min-h-8 w-20 shrink-0 break-words rounded p-1 text-left text-xs text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default disabled:hover:bg-transparent sm:w-24">
            {lane.label}
          </button>
          <div className="relative h-7 min-w-0 flex-1 rounded bg-gray-50">
            {lane.bars.map((b) => {
              const left = pct(b.start_on);
              const right = pct(b.end_on);
              if (right <= left) return null;
              return (
                <button
                  type="button"
                  key={barKey(b)}
                  title={b.tip}
                  aria-label={`${lane.label} · ${b.text || ''} · ${b.tip}`}
                  aria-pressed={selectedLane?.key === lane.key && selection.bar === barKey(b)}
                  aria-controls={detailId}
                  onClick={() => select(lane.key, barKey(b))}
                  className={`absolute inset-y-1 min-w-0 rounded p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${BAR_TONE[b.tone] || BAR_TONE.cur} ${selectedLane?.key === lane.key && selection.bar === barKey(b) ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                  style={{ left: `${left}%`, width: `${right - left}%` }}
                />
              );
            })}
            <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-px bg-danger" style={{ left: `${todayPct}%`, transform: 'translateX(-100%)' }} />
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <span className="w-20 shrink-0 sm:w-24" />
        <div ref={axisRef} className="relative h-4 min-w-0 flex-1">
          {ticks.map((t) => (
            <span key={t.date} aria-hidden={hiddenTicks.includes(t.date) || undefined} className="absolute top-0 whitespace-nowrap text-[11px] text-gray-400" style={{ left: `${pct(t.date)}%`, transform: pct(t.date) > 90 ? 'translateX(-100%)' : undefined, visibility: hiddenTicks.includes(t.date) ? 'hidden' : undefined }}>{t.label}</span>
          ))}
        </div>
      </div>
      <p className="pt-2 text-[11px] text-gray-500">막대나 왼쪽 이름을 누르면 기간 정보를 볼 수 있습니다.</p>
      <div id={detailId} aria-live="polite">
        {selectedBars.length > 0 && (
          <section aria-label="기간 상세" className="mt-2 min-w-0 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="mb-2 flex items-start justify-between gap-2">
              <h5 className="min-w-0 break-words font-semibold text-gray-900">{selectedLane.label}</h5>
              <button type="button" onClick={() => setSelection(null)} className="shrink-0 rounded px-2 py-1 text-gray-500 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">접기</button>
            </div>
            <ul className="space-y-2">
              {selectedBars.map((bar) => (
                <li key={barKey(bar)} className="break-words rounded-lg bg-white p-2 leading-relaxed">
                  {!selectedLane.key.startsWith('class-') && bar.text && bar.text !== selectedLane.label && <p className="font-medium text-gray-900">{bar.text}</p>}
                  <p className="text-gray-700">{fullDate(bar.start_on)} ~ {(bar.end_inclusive ? bar.display_end_on : bar.end_on) ? fullDate(bar.end_inclusive ? bar.display_end_on : bar.end_on) : `현재 (${fullDate(today)})`}</p>
                  {bar.summary && <p className="text-gray-500">{bar.summary}</p>}
                </li>
              ))}
            </ul>
            {selectedBars.some((bar) => bar.end_on || bar.display_end_on) && <p className="mt-2 text-[11px] text-gray-500">{selectedLane.key === 'tenure' ? '퇴원일은 재원에 포함되는 마지막 수강일입니다.' : selectedLane.key.startsWith('class-') ? '퇴반일은 해당 반에서 수강한 마지막 날입니다.' : '종료일은 해당 상태가 아닌 첫날입니다.'}</p>}
          </section>
        )}
      </div>
    </div>
  );
}

export default PeriodLanes;
